import { beSalonStripeAccountWrite } from 'actions/salon/bePutSalonStripe.action';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import BULLET_POINTS from 'constants/bulletPoints.const';
import useCallbackUnignored from 'hooks/useCallbackUnignored.hook';
import { usePrepareWriteOnly } from 'hooks/usePrepare.hook';
import { useTranslation } from 'providers/GILT.provider';
import { locationAssign, locationReload } from 'utils/dom/location.util';
import logger from 'utils/logger.util';
import { isValidUrl } from 'utils/predicates/isValid.util';

import CN from './BeforeSetupStripeModal.module.scss';

const CTAG = 'BeforeSetupStripeModal';
const L = logger(CTAG); // eslint-disable-line no-unused-vars

const BeforeSetupStripeModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { up: getStripeLink, isLoading } = usePrepareWriteOnly(beSalonStripeAccountWrite);

  const handleAcceptContinue = useCallbackUnignored(
    async () => {
      const FTAG = 'handleAcceptContinue()';

      const result = await getStripeLink();

      const url = result?.json?.stripeLink || result?.originalJson?.results?.stripe_link || result?.originalJson?.stripe_link || null;

      if (url) {
        L.debug(FTAG, 'Opening stripe URL:', url);
        locationAssign({ url });
        return;
      }

      onClose();
      locationReload({ tag: 'BeforeSetupStripeModal' });

      if (!isValidUrl(url)) L.warn(FTAG, 'Invalid stripe url:', url);

      if (result?.ok) {
        L.warn(FTAG, 'Stripe link not received.');
      } else {
        L.warn(FTAG, 'Stripe setup failed.', 'result:', result);
      }
    },
    [getStripeLink, onClose],
    CTAG,
    'handleAcceptContinue()',
  );

  if (isLoading) return <OverlaySpinner text={t('onlineBooking.generalSettings.businessStripeAccount.stripeModalText')} />;

  return (
    <SalonModal tag="BeforeSetupStripeModal-modal" isOpen={isOpen}>
      <SalonModal.Header>
        <div className={CN.closeButtonContainer}>
          <Button variant="empty" onClick={onClose}>
            <Icon variant="close" variantInvert />
          </Button>
        </div>
      </SalonModal.Header>
      <SalonModal.Body>
        <div className={CN.scroll}>
          <div className={CN.modalImage}>
            <div className={CN.iconContainer}>
              <Icon variant="identification" />
            </div>
          </div>
          <div className={CN.modalContent}>
            <div className={CN.modalTitle}>{t('composite.beforeSetupStripeModal.title')}</div>
            <div className={CN.modalSubTitle}>{t('composite.beforeSetupStripeModal.description')}</div>
            <div className={CN.modalText}>
              <div className={CN.item}>
                <div className={CN.number}>{BULLET_POINTS.one}</div>
                <div>
                  <div className={CN.textBold}>{t('composite.beforeSetupStripeModal.photoId')}</div>
                  {t('composite.beforeSetupStripeModal.photoIdDocs')}
                </div>
              </div>
              <div className={CN.item}>
                <div className={CN.number}>{BULLET_POINTS.two}</div>
                <div>
                  <div className={CN.textBold}>{t('composite.beforeSetupStripeModal.proofAddress')}</div>
                  {t('composite.beforeSetupStripeModal.proofAddressDocs')}
                </div>
              </div>
              <div className={CN.item}>
                <div className={CN.number}>{BULLET_POINTS.three}</div>
                <div>
                  <div className={CN.textBold}>{t('composite.beforeSetupStripeModal.companyNumber')}</div>
                  {t('composite.beforeSetupStripeModal.companyNumberDocs')}
                </div>
              </div>
            </div>
            <div className={CN.info}>{t('composite.beforeSetupStripeModal.payoutsNote')}</div>
          </div>
        </div>
      </SalonModal.Body>
      <SalonModal.Footer>
        <Button variant="primaryForce" onClick={handleAcceptContinue} xSpace>
          {t('composite.beforeSetupStripeModal.confirm')}
        </Button>
      </SalonModal.Footer>
    </SalonModal>
  );
};

export default BeforeSetupStripeModal;
